/**
 * Halloween garlands — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const halloweenGarlandsPack: ToilePack = {
    slug: 'halloween-garlands',
    category: 'season',
    name: 'Halloween garlands',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'bats', label: 'Bat garland', subject: 'A sheet of paper bats in two sizes with hole marks to thread on a string, dashed cut lines, no lettering'},
        {key: 'pumpkins', label: 'Pumpkin garland', subject: 'A sheet of small paper pumpkins to cut and string, each with a different face, dashed cut lines, no lettering'},
        {key: 'ghosts', label: 'Ghost chain', subject: 'A sheet of paper ghosts holding hands as a chain to cut and hang, dashed cut lines, no lettering'},
    ],
    styles: [
        {key: 'classic', label: 'Classic', style: 'classic halloween printable, bat and pumpkin silhouettes, playful spooky lettering, print-ready', palette: 'Orange, black and white', framing: 'Laid out on a vertical page with an even white margin'},
        {key: 'gothic', label: 'Gothic', style: 'gothic halloween printable, ornate filigree frames, elegant serif lettering, print-ready', palette: 'Black, deep purple, bone white and a little gold', framing: 'Laid out on a dark page with an ornate border and a margin'},
        {key: 'kids', label: 'Kids', style: "kids' halloween printable, friendly cartoon monsters, rounded lettering, print-ready", palette: 'Bright orange, purple, lime green and white', framing: 'Laid out on a vertical page with a playful border and a white margin'},
    ],
    directions: ['as a spiderweb chain', 'in miniature for a mantel', 'with glow-in-the-dark hints'],
    directionHint: 'try: as a spiderweb chain',
    seo: {
        title: 'Halloween garland: bats, pumpkins and ghosts',
        metaDescription: "Nine Halloween paper garlands — bats, pumpkins, a chain of ghosts — cut lines drawn, classic, gothic or kids' style, ready to string.",
        h1: 'Halloween garland: bats, pumpkins and ghosts in three styles',
        tagline: 'Nine sheets, three garland motifs, three styles each — cut, string, and hang.',
        intro: "Three Halloween paper garlands to cut and string: bats along a line, paper pumpkins, a chain of ghosts. Every sheet marks its cut lines, drawn classic orange-and-black, gothic elegant, or bright kids' style. Print on card stock, cut out the shapes, and thread them on twine or ribbon. Type a different creature and the set redraws.",
        useCases: [
            'a Halloween party wall or mantel decoration',
            'a classroom bulletin board garland',
            'a front-window or doorway hang',
            'a DIY Halloween decoration on a budget',
        ],
        faq: [
            {q: 'What do I need to assemble the garland?', a: 'Cut out the shapes along the drawn lines, punch a small hole where marked, and thread twine, ribbon, or string through in sequence.'},
            {q: 'What paper should I print on?', a: 'Card stock holds its shape best for hanging; standard printer paper works but is more likely to sag or tear.'},
            {q: 'Can I change the creature or the color?', a: "Yes — type a direction like 'add a spider' or 'in purple and black' and the garland redraws in the same three styles."},
            {q: 'How long is the finished garland?', a: 'Each sheet yields enough shapes for a short garland; print multiple sheets and string them together for a longer run.'},
        ],
    },
}
